import { InputTextArea, MultipleButtonFloating } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useCaseMutation from "@/hooks/useCaseMutation";
import { MedicalNoteFormSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { useCallback } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/medical-note/create",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const onBack = useBackward();

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<MedicalNoteFormSchema>({
		mode: "all",
		resolver: zodResolver(MedicalNoteFormSchema),
		defaultValues: {
			id: caseId,
			text: "",
		},
	});

	const { createMedicalNote } = useCaseMutation();

	const onSubmit = useCallback<SubmitHandler<MedicalNoteFormSchema>>(
		(data, e) => {
			e?.stopPropagation();
			e?.preventDefault();
			return createMedicalNote.mutate(data, {
				onSuccess: () => {
					toast.success("Medical note added");
					return onBack();
				},
				onError: () => {
					toast.error("Failed to create medical note");
				},
			});
		},
		[onBack, createMedicalNote],
	);

	return (
		<>
			<BackAppBar title="Add Medical Note" />
			<form onSubmit={handleSubmit(onSubmit)} className=" bg-white h-full">
				<div className="container flex flex-col gap-4 py-4">
					<Controller
						control={control}
						name="text"
						render={({ field, fieldState }) => (
							<InputTextArea
								fullWidth
								label="Medical Note"
								minRows={4}
								error={!!fieldState.error?.message}
								{...omit(field, ["ref"])}
							/>
						)}
					/>
				</div>
				<MultipleButtonFloating
					negativeButtonProps={{
						label: "Cancel",
						type: "button",
						onClick: () => onBack(),
					}}
					positiveButtonProps={{
						label: "Submit",
						type: "submit",
						disabled: !isDirty || !isValid || isSubmitting,
					}}
				/>
			</form>
		</>
	);
}
